import { useMutation, useQuery } from '@tanstack/react-query';

/**
 * Base URL for backend calls. Matches the marketplace's convention
 * (apps/marketplace/src/api/use-marketplace.ts) so the prepaid-booking
 * endpoints hit the same origin and the `cb_market_session` cookie rides
 * along with `credentials: 'include'`.
 */
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? 'https://business.clever-booking.com';

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Accept-Language':
        typeof navigator !== 'undefined' ? navigator.language || 'en' : 'en',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    const err = new Error(text || `${r.status} ${r.statusText}`) as Error & {
      status: number;
    };
    err.status = r.status;
    throw err;
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// ─── Types ───────────────────────────────────────────────────────────────

/**
 * Mirrors the backend `BookingIntentRequest`. Note: the backend takes a
 * single `customerName` (NOT firstName/lastName) and an ISO LocalDateTime
 * string for `startTime` (no timezone offset).
 */
export interface BookingPayload {
  serviceId: string;
  locationId?: string | null;
  employeeId?: string | null;
  bookingMode: string;
  startTime: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  notes?: string;
  /**
   * REQUIRED by the backend prepaid replay: the customer must have agreed to
   * the cancellation policy before the booking-intent is created, else the
   * post-payment booking replay rejects with 400. Always send `true` (the UI
   * gates the pay action behind the policy checkbox).
   */
  agreedToPolicy?: boolean;
  /** Email-OTP proof for prepaid marketplace bookings; ignored by client-booking. */
  verificationToken?: string;
  /**
   * IANA timezone (e.g. "Europe/Brussels"). REQUIRED by the backend for ONLINE
   * prepaid bookings: the post-payment replay runs validateBookable, which
   * rejects an ONLINE hold with CUSTOMER_TIMEZONE_REQUIRED when it's absent.
   * Callers send the browser-detected zone for ONLINE.
   */
  customerTimezone?: string;
  /**
   * UI language (i18next code, e.g. "nl", "pt-BR"). Lets the backend localize
   * downstream notifications for this booking. Optional; omitted → BE default.
   */
  language?: string;
  /**
   * Marketing consent captured at booking time (#162). Only sent when the
   * customer ticked the box; omitting it means "no change" on the backend, so
   * we never send `false` (that would silently downgrade an existing opt-in).
   */
  marketingOptIn?: boolean;
}

export interface BookingIntentResult {
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  currency: string;
}

export interface BookingIntentStatus {
  state: 'pending' | 'booked' | 'failed' | 'refunded';
  appointmentId?: string;
  bookingReference?: string;
}

// ─── Hooks ─────────────────────────────────────────────────────────────────

/**
 * Creates a prepaid-booking PaymentIntent for the given salon. Returns the
 * Stripe `clientSecret` (for the Payment Element) plus `paymentIntentId`
 * (used to poll booking status after payment confirms).
 */
export function useCreateBookingIntent(subdomain: string) {
  return useMutation<BookingIntentResult, Error, BookingPayload>({
    mutationFn: (payload: BookingPayload) =>
      api<BookingIntentResult>(
        `/api/v1/marketplace/salon/${subdomain}/booking-intent`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      ),
  });
}

/**
 * Polls the booking-intent status every 2s while the state is `pending`.
 * Disabled until a `paymentIntentId` is provided. Polling stops automatically
 * once the booking resolves to a terminal state (booked/failed/refunded).
 */
export function useBookingIntentStatus(paymentIntentId: string | null) {
  return useQuery<BookingIntentStatus>({
    queryKey: ['booking-intent-status', paymentIntentId],
    enabled: !!paymentIntentId,
    queryFn: () =>
      api<BookingIntentStatus>(
        `/api/v1/marketplace/booking-intent/${paymentIntentId}/status`,
      ),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && data.state !== 'pending' ? false : 2000;
    },
  });
}
